const toFiniteNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const packageEnabled = (value: unknown): boolean => (
  value === true || String(value).trim().toLowerCase() === 'true'
);

const packageCount = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const match = String(value).trim().match(/^\d+(?:\.\d+)?/);
  return match ? toFiniteNumber(match[0]) : undefined;
};

export interface UnitLoadTotals {
  totalCasesPerUnitLoad?: number;
  itemsPerPallet?: number;
}

type CalculationSection = Record<string, unknown>;

interface UnitLoadCalculationInput {
  unitLoadDetails?: CalculationSection;
  secondaryPackagingDetails?: CalculationSection;
  secondaryPackaging?: CalculationSection;
  tertiaryPackagingDetails?: CalculationSection;
  tertiaryPackaging?: CalculationSection;
}

export const calculateUnitLoadTotals = (values: UnitLoadCalculationInput): UnitLoadTotals => {
  const unitLoad = values?.unitLoadDetails ?? {};
  const secondary = values?.secondaryPackagingDetails ?? values?.secondaryPackaging ?? {};
  const tertiary = values?.tertiaryPackagingDetails ?? values?.tertiaryPackaging ?? {};
  const casesPerLayer = toFiniteNumber(unitLoad.unitCasesPerLayer);
  const layersPerUnitLoad = toFiniteNumber(unitLoad.unitLayersPerUnitLoad);

  if (casesPerLayer === undefined || layersPerUnitLoad === undefined) return {};

  const totalCasesPerUnitLoad = casesPerLayer * layersPerUnitLoad;
  let multiplier = 1;
  let hasPackagedItems = false;

  if (packageEnabled(secondary.secondaryPkg)) {
    const count = packageCount(secondary.secondaryPackageCase);
    if (count === undefined) return { totalCasesPerUnitLoad };
    multiplier *= count;
    hasPackagedItems = true;
  }

  if (packageEnabled(tertiary.tertiaryPkg)) {
    const count = packageCount(tertiary.tertiarySecondaryPackagesPerUnit);
    if (count === undefined) return { totalCasesPerUnitLoad };
    multiplier *= count;
    hasPackagedItems = true;
  }

  return {
    totalCasesPerUnitLoad,
    itemsPerPallet: hasPackagedItems ? totalCasesPerUnitLoad * multiplier : undefined,
  };
};

export const withCalculatedUnitLoadTotals = <T extends UnitLoadCalculationInput>(values: T) => {
  const calculated = calculateUnitLoadTotals(values);
  return {
    ...values,
    unitLoadDetails: {
      ...values?.unitLoadDetails,
      ...(calculated.totalCasesPerUnitLoad !== undefined
        ? { totalCasesPerUnitLoad: calculated.totalCasesPerUnitLoad }
        : {}),
      ...(calculated.itemsPerPallet !== undefined
        ? { itemsPerPallet: calculated.itemsPerPallet }
        : {}),
    },
  };
};
